import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Printer, ExternalLink, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS = {
  fehlt: { text: "Es fehlt", cls: "bg-[#FBEDE7] text-[#B4502F] border-[#F0C9B8]" },
  druckbereit: { text: "Druckbereit", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  original_offen: { text: "Original-Druck offen", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  gedruckt: { text: "Gedruckt", cls: "bg-stone-100 text-stone-600 border-stone-200" },
};
const fmt = (d) => (d ? new Date(d).toLocaleDateString("de-DE") : "–");

const drucken = async (z, rg, onDone) => {
  const neu = z.status === "druckbereit";
  const msg = neu
    ? `ORIGINAL-Zertifikat für ${z.name}\nKurs: ${z.kurs_name}\nRechtsgrundlage: ${rg === "hessen" ? "Hessen" : "NRW"}\n\n• Neue fortlaufende Nummer wird vergeben\n• Original-Datum wird festgeschrieben\n\nBitte Edelpapier einlegen. Fortfahren?`
    : `ORIGINAL auf Edelpapier für ${z.name} drucken (gleiche Nummer ${z.zertifikatsnummer})?`;
  if (!window.confirm(msg)) return;
  try {
    const p = new URLSearchParams({ rechtsgrundlage: rg, original: "true" });
    if (z.kurs_id) p.set("kurs_id", z.kurs_id);
    const r = await axios.get(`${API}/teilnehmer/${z.teilnehmer_id}/zertifikat-pdf?${p}`, { responseType: "blob" });
    window.open(URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })), "_blank");
    toast.success(`Zertifikat ${r.headers?.["x-zertifikats-nr"] || ""} erstellt`);
    onDone();
  } catch (e) {
    let m = "Druck fehlgeschlagen";
    try { const t = await e.response?.data?.text?.(); if (t) m = JSON.parse(t).detail || m; } catch (_) {}
    toast.error(m);
  }
};

const Zeile = ({ z, onDone }) => {
  const [rg, setRg] = useState(z.rechtsgrundlage || "nrw");
  const s = STATUS[z.status];
  const id = `${z.teilnehmer_id}-${z.kurs_id}`;
  return (
    <tr className="border-t border-stone-200 align-top" data-testid={`druckliste-zeile-${id}`}>
      <td className="py-2 pr-3"><a href={`/teilnehmer?id=${z.teilnehmer_id}`} className="font-medium text-stone-900 hover:underline inline-flex items-center gap-1">{z.name || "(ohne Name)"} <ExternalLink size={11} /></a></td>
      <td className="py-2 pr-3 text-stone-700">{z.kurs_name}{z.rezert && <span className="ml-1 text-xs text-stone-500">(Rezi – ohne Prüfung)</span>}<div className="text-xs text-stone-500">{fmt(z.start)} – {fmt(z.ende)}</div></td>
      <td className="py-2 pr-3">
        <span className={`inline-block rounded-full border px-2 py-0.5 text-xs font-medium ${s.cls}`} data-testid={`druckliste-status-${id}`}>{s.text}{z.status === "gedruckt" && z.zertifikatsnummer ? ` · ${z.zertifikatsnummer}` : ""}</span>
        {z.fehlt.length > 0 && <div className="text-xs text-[#B4502F] mt-1" data-testid={`druckliste-fehlt-${id}`}>{z.fehlt.join(", ")}</div>}
      </td>
      <td className="py-2 text-right whitespace-nowrap">
        {["druckbereit", "original_offen"].includes(z.status) && (
          <div className="inline-flex items-center gap-2">
            <select value={rg} onChange={(e) => setRg(e.target.value)} className="h-8 rounded border border-stone-300 bg-white px-1 text-xs" data-testid={`druckliste-rg-${id}`}>
              <option value="nrw">NRW</option><option value="hessen">Hessen</option>
            </select>
            <Button size="sm" onClick={() => drucken(z, rg, onDone)} data-testid={`druckliste-drucken-${id}`}><Printer size={14} className="mr-1" /> Drucken</Button>
          </div>
        )}
      </td>
    </tr>
  );
};

export const ZertifikatDruckliste = () => {
  const [liste, setListe] = useState(null);
  const [nurOffen, setNurOffen] = useState(true);
  const laden = () => axios.get(`${API}/zertifikat-druckliste`).then((r) => setListe(r.data)).catch(() => setListe([]));
  useEffect(() => { laden(); }, []);
  const anz = (st) => (liste || []).filter((z) => z.status === st).length;
  const sichtbar = (liste || []).filter((z) => !nurOffen || z.status !== "gedruckt");
  return (
    <div className="mb-8 rounded-xl border border-stone-200 bg-white p-5" data-testid="zertifikat-druckliste">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div>
          <h2 className="text-base md:text-lg font-semibold text-stone-900">Druckliste Zertifikate</h2>
          <p className="text-xs text-stone-600" data-testid="druckliste-zaehler">
            {liste ? `${anz("fehlt")} mit Lücken · ${anz("druckbereit")} druckbereit · ${anz("original_offen")} Original offen · ${anz("gedruckt")} gedruckt` : "Wird geladen …"}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-xs text-stone-700"><input type="checkbox" checked={nurOffen} onChange={(e) => setNurOffen(e.target.checked)} data-testid="druckliste-nur-offen" /> Gedruckte ausblenden</label>
          <button onClick={laden} className="text-stone-500 hover:text-stone-900" title="Neu laden" data-testid="druckliste-neu-laden"><RefreshCw size={15} /></button>
        </div>
      </div>
      {liste && !sichtbar.length && <p className="text-sm text-stone-500" data-testid="druckliste-leer">Keine offenen Zertifikate.</p>}
      {sichtbar.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-stone-500"><th className="pb-2">Teilnehmer</th><th className="pb-2">Kurs</th><th className="pb-2">Status</th><th /></tr></thead>
            <tbody>{sichtbar.map((z) => <Zeile key={`${z.teilnehmer_id}-${z.kurs_id}`} z={z} onDone={laden} />)}</tbody>
          </table>
        </div>
      )}
    </div>
  );
};
